import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Alert,
  Paper,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { importService } from '../services/api';

export default function ImportPage() {
  const [searchParams] = useSearchParams();
  const entityFromUrl = searchParams.get('entity') || '';
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(entityFromUrl);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [resultMsg, setResultMsg] = useState(null); // { severity, text }

  const selected = useMemo(() => entities.find((e) => e.key === entity) || null, [entities, entity]);

  useEffect(() => {
    if (entityFromUrl) setEntity(entityFromUrl);
  }, [entityFromUrl]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    importService
      .entities()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.results || [];
        setEntities(list);
        const preferred = entityFromUrl || entity;
        if (preferred && list.some((e) => e.key === preferred)) {
          setEntity(preferred);
        } else if (!entity && list.length) {
          setEntity(list[0].key);
        }
      })
      .catch((e) => {
        console.error(e);
        alert('Помилка завантаження списку сутностей для імпорту');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    if (!entity || !file) return;
    setBusy(true);
    try {
      const res = await importService.preview(entity, file);
      const data = res?.data;
      setPreview(data);
      setResultMsg(null);
      const headers = Array.isArray(data?.headers) ? data.headers : [];
      const next = {};
      headers.forEach((h) => {
        next[h] = '__skip__';
      });
      setMapping(next);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || e.message || 'Помилка превʼю');
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!entity || !file || !preview) return;
    const used = Object.values(mapping).filter((v) => v && v !== '__skip__');
    if (used.length === 0) {
      alert('Зроби мапінг хоча б для однієї колонки.');
      return;
    }
    setBusy(true);
    try {
      const res = await importService.run(entity, file, mapping);
      const created = res.data?.created ?? 0;
      const updated = res.data?.updated ?? 0;
      const skipped = res.data?.skipped ?? 0;
      setResultMsg({
        severity: 'success',
        text: `Імпорт успішний. Створено: ${created}, оновлено: ${updated}, пропущено: ${skipped}.`,
      });
      setPreview(null);
      setMapping({});
    } catch (e) {
      console.error(e);
      const d = e.response?.data;
      if (d?.errors?.length) {
        setResultMsg({
          severity: 'error',
          text: `Імпорт з помилками. Показано перші помилки: ${JSON.stringify(d.errors.slice(0, 3))}`,
        });
      } else {
        setResultMsg({ severity: 'error', text: d?.detail || e.message || 'Помилка імпорту' });
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  const availableFields = preview?.available_fields || selected?.fields || [];

  return (
    <Box sx={{ p: 2, maxWidth: 1200 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Імпорт
      </Typography>
      {resultMsg ? (
        <Alert severity={resultMsg.severity} sx={{ mb: 2 }}>
          {resultMsg.text}
        </Alert>
      ) : null}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Сутність"
            value={entity}
            onChange={(e) => {
              setEntity(e.target.value);
              setPreview(null);
              setMapping({});
            }}
            sx={{ minWidth: 320 }}
          >
            {entities.map((e) => (
              <MenuItem key={e.key} value={e.key}>
                {e.label}
              </MenuItem>
            ))}
          </TextField>

          <Button variant="outlined" component="label" disabled={busy}>
            {file ? `Файл: ${file.name}` : 'Завантажити Excel'}
            <input
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                setPreview(null);
                setMapping({});
              }}
            />
          </Button>

          <Button variant="contained" onClick={runPreview} disabled={!entity || !file || busy}>
            {busy ? 'Завантажуємо…' : 'Превʼю'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Перший рядок Excel — це назви колонок. Далі зроби мапінг “колонка → поле” і запускай імпорт.
        </Typography>
      </Paper>

      {preview ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Мапінг колонок
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Колонка з Excel</TableCell>
                  <TableCell>Поле в системі</TableCell>
                  <TableCell>Приклад (1-й рядок)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(preview.headers || []).map((h, idx) => (
                  <TableRow key={h || idx}>
                    <TableCell>{h || `Колонка ${idx + 1}`}</TableCell>
                    <TableCell sx={{ minWidth: 360 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={mapping[h] ?? '__skip__'}
                        onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                      >
                        <MenuItem value="__skip__">— пропустити —</MenuItem>
                        {availableFields.map((f) => (
                          <MenuItem key={f.key} value={f.key}>
                            {f.label} {f.kind === 'fk' ? '(ID)' : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {Array.isArray(preview.sample_rows) && preview.sample_rows[0]
                        ? preview.sample_rows[0][idx] ?? ''
                        : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="success" onClick={runImport} disabled={busy}>
              Імпортувати
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setPreview(null);
                setMapping({});
              }}
              disabled={busy}
            >
              Скинути превʼю
            </Button>
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}

